import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtMoney, logoSrc } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import {
  Landmark, TrendingUp, TrendingDown, Coins, Trophy, Star, Building2,
  Tv, Shirt, Sparkles, Check, RefreshCcw, Save, ChevronDown, ChevronUp, Wallet,
  BarChart3, Users2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  RadialBarChart, RadialBar, PolarAngleAxis, ComposedChart, Line, Area, Legend,
} from "recharts";

const money = (v) => fmtMoney(Math.round(Number(v || 0)));

export default function LeagueFinanze() {
  const { league, myTeam, teams, isAdmin } = useLeague();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState(myTeam?.team_id || "");
  const [applyBusy, setApplyBusy] = useState(false);
  const [viewMode, setViewMode] = useState("single"); // "single" | "compare"

  const reload = async () => {
    setLoading(true);
    try {
      const [finR, histR] = await Promise.all([
        api.get(`/leagues/${league.league_id}/finances`),
        api.get(`/leagues/${league.league_id}/history`),
      ]);
      setData(finR.data);
      setHistory(histR.data || []);
      if (!selectedTeamId && finR.data.teams?.length) {
        setSelectedTeamId(myTeam?.team_id || finR.data.teams[0].team_id);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [league.league_id]);

  const financeApplied = league.finance_applied_season === league.season;

  const applySaldo = async () => {
    if (!window.confirm(`Applicare il saldo Finanze alla stagione ${league.season}?\nOgni squadra vedrà aggiornati budget e ranking storico.`)) return;
    setApplyBusy(true);
    try {
      await api.post(`/leagues/${league.league_id}/finances/apply`);
      toast.success("💰 Saldo Finanze applicato con successo!");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplyBusy(false); }
  };

  const resetFlag = async () => {
    if (!window.confirm("Sbloccare la possibilità di ri-applicare il saldo per questa stagione?")) return;
    try {
      await api.post(`/leagues/${league.league_id}/finances/reset-flag`);
      toast.success("Flag saldo resettato");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const selected = data?.teams?.find(t => t.team_id === selectedTeamId) || data?.teams?.[0];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-emerald-400 uppercase font-black flex items-center gap-2">
            <Landmark size={14}/> Modulo Finanze
          </div>
          <h2 className="text-3xl font-black tracking-tight mt-1">💰 Bilancio della Società</h2>
          <p className="text-slate-400 text-sm mt-1">Contabilità automatica: patrimonio, entrate, uscite, saldo di fine stagione.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Toggle vista */}
          <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden" data-testid="finanze-view-toggle">
            <button
              onClick={()=>setViewMode("single")}
              data-testid="view-single-btn"
              className={`px-3 py-2 text-xs font-black uppercase tracking-wider flex items-center gap-1 ${
                viewMode === "single" ? "bg-emerald-500 text-slate-950" : "bg-slate-950 text-slate-400 hover:text-white"
              }`}>
              <Landmark size={12}/> Singola
            </button>
            <button
              onClick={()=>setViewMode("compare")}
              data-testid="view-compare-btn"
              className={`px-3 py-2 text-xs font-black uppercase tracking-wider flex items-center gap-1 ${
                viewMode === "compare" ? "bg-emerald-500 text-slate-950" : "bg-slate-950 text-slate-400 hover:text-white"
              }`}>
              <Users2 size={12}/> Confronto
            </button>
          </div>
          {viewMode === "single" && (
            <select
              data-testid="finanze-team-select"
              value={selectedTeamId || ""}
              onChange={(e)=>setSelectedTeamId(e.target.value)}
              className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-bold text-white focus:outline-none focus:border-emerald-500"
            >
              {data?.teams?.map(t => <option key={t.team_id} value={t.team_id}>{t.team_name}</option>)}
            </select>
          )}
          <button onClick={reload} title="Ricarica"
            className="p-2 rounded-lg border border-slate-700 text-slate-400 hover:text-emerald-400"
            data-testid="finanze-reload">
            <RefreshCcw size={14}/>
          </button>
        </div>
      </div>

      {/* Admin: piazzamenti competizioni + Apply saldo */}
      {isAdmin && (
        <AdminResultsPanel
          leagueId={league.league_id}
          season={league.season}
          competitions={league.settings?.competitions || []}
          teams={teams}
          currentResults={data?.season_results || {}}
          financeApplied={financeApplied}
          onSaved={reload}
          onApply={applySaldo}
          onResetFlag={resetFlag}
          applyBusy={applyBusy}
        />
      )}

      {loading || !data ? (
        <div className="text-slate-500 text-sm py-8 text-center">Caricamento bilancio…</div>
      ) : viewMode === "compare" ? (
        <CompareTable data={data} teams={teams} />
      ) : !selected ? (
        <div className="text-slate-500 text-sm py-8 text-center">Nessuna squadra disponibile.</div>
      ) : (
        <>
          {/* Patrimonio */}
          <PatrimonioSection s={selected} />
          {/* Entrate + Uscite */}
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <EntrateSection s={selected} />
              <UsciteSection s={selected} />
            </div>
            <BudgetFinaleCard s={selected} />
          </div>
          {/* Barchart entrate breakdown */}
          <RevenueBreakdown s={selected} />

          {/* Storico voci multi-season */}
          <MultiSeasonHistory history={history} teamId={selectedTeamId} teamName={selected.team_name} />

          {/* Team Journey AI */}
          <TeamJourneyPanel leagueId={league.league_id} teamId={selectedTeamId} teamName={selected.team_name} nHistorySeasons={history.length}/>
        </>
      )}
    </div>
  );
}

/* -------------------- Sotto-componenti -------------------- */

function KpiPill({ label, value, sub, tone="slate", emoji }) {
  const toneMap = {
    slate: "border-slate-800 text-white",
    emerald: "border-emerald-500/40 text-emerald-300",
    amber: "border-amber-500/40 text-amber-300",
    rose: "border-rose-500/40 text-rose-300",
    indigo: "border-indigo-500/40 text-indigo-300",
    fuchsia: "border-fuchsia-500/40 text-fuchsia-300",
  };
  return (
    <div className={`rounded-2xl border ${toneMap[tone]} bg-slate-950/70 p-4`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
        {emoji && <span>{emoji}</span>} {label}
      </div>
      <div className="text-2xl font-mono font-black mt-1">{value}</div>
      {sub != null && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function PatrimonioSection({ s }) {
  const p = s.patrimonio;
  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-emerald-900/30 via-slate-900 to-slate-950 p-5 sm:p-6 shadow-2xl shadow-black/40" data-testid="patrimonio-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-black">🏦 Patrimonio</span>
        <span className="text-slate-500 text-xs">— {s.team_name}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiPill emoji="💰" label="Budget" value={`${money(p.budget)}`} sub="cassa liquida"/>
        <KpiPill emoji="📊" label="Valore rosa" value={money(p.valore_rosa)} sub={`${p.n_giocatori} attivi`} tone="emerald"/>
        <KpiPill emoji="💵" label="Totale pagato" value={money(p.totale_pagato)} sub="somma acquisti"/>
        <KpiPill emoji={p.plus_minus >= 0 ? "↗" : "↘"} label="Plus/Minus" value={money(p.plus_minus)}
          sub={`${p.growth_pct >= 0 ? "+" : ""}${p.growth_pct.toFixed(1)}% crescita`}
          tone={p.plus_minus >= 0 ? "emerald" : "rose"} />
        <KpiPill emoji="🎯" label="ROI medio" value={`${p.avg_roi_pct.toFixed(1)}%`} sub="per giocatore" tone={p.avg_roi_pct >= 0 ? "emerald" : "rose"}/>
        <KpiPill emoji="🏆" label="Patrimonio tot." value={money(p.patrimonio_totale)} sub="Budget + Rosa" tone="amber"/>
      </div>
    </div>
  );
}

function EntrateSection({ s }) {
  const e = s.entrate;
  const rows = [
    { icon: <Tv size={14}/>, label: "Diritti TV", val: e.tv_rights, color: "text-sky-300", details: s.tv_rights?.detail },
    { icon: <Shirt size={14}/>, label: "Merchandising base", val: e.merch_base, color: "text-fuchsia-300", details: s.merchandising?.detail?.slice(0, 5).map(x => ({ label: `${x.name} (${x.cost}cr)`, amount: x.bonus })) },
    { icon: <Sparkles size={14}/>, label: "Rivalutazione merch", val: e.merch_revaluation, color: "text-fuchsia-200",
      details: s.merchandising?.revaluation?.rules_applied?.map(r => ({ label: r.label, amount: `x${r.multiplier.toFixed(2)}` })) },
    { icon: <Trophy size={14}/>, label: "Premi sportivi", val: e.prizes, color: "text-amber-300", details: s.prizes?.detail?.map(d => ({ label: `${d.emoji} ${d.competition_name} · ${d.position}°`, amount: d.amount })) },
    { icon: <Building2 size={14}/>, label: "Ricavo stadio (netto)", val: e.stadium_net, color: "text-orange-300",
      details: [
        { label: "Ricavo base", amount: s.stadium?.base_income || 0 },
        ...(s.stadium?.bonus_applied || []).map(b => ({ label: b.label, amount: `x${b.multiplier.toFixed(2)}` })),
        { label: "Manutenzione", amount: -Math.abs(s.stadium?.maintenance || 0) },
      ]},
    { icon: <Coins size={14}/>, label: "Voci extra", val: e.extra_total, color: "text-emerald-300", details: s.extra_entries },
  ];
  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="entrate-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
          <TrendingUp size={16} className="text-emerald-400"/> Entrate stagione
        </h3>
        <span className="text-2xl font-mono font-black text-emerald-300">+{money(e.total)}<span className="text-xs text-slate-500 ml-1">cr</span></span>
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <ExpandableRow key={i} icon={r.icon} label={r.label} value={r.val} color={r.color} details={r.details}/>
        ))}
      </div>
    </div>
  );
}

function UsciteSection({ s }) {
  const u = s.uscite;
  const rows = [
    { icon: <Wallet size={14}/>, label: `Stipendi (${s.salaries?.n_paid} giocatori)`, val: u.salaries, color: "text-rose-300" },
    { icon: <Building2 size={14}/>, label: "Altre uscite", val: u.other, color: "text-slate-400" },
  ];
  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="uscite-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
          <TrendingDown size={16} className="text-rose-400"/> Uscite stagione
        </h3>
        <span className="text-2xl font-mono font-black text-rose-300">-{money(u.total)}<span className="text-xs text-slate-500 ml-1">cr</span></span>
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <ExpandableRow key={i} icon={r.icon} label={r.label} value={-Math.abs(r.val)} color={r.color}/>
        ))}
      </div>
    </div>
  );
}

function ExpandableRow({ icon, label, value, color, details }) {
  const [open, setOpen] = useState(false);
  const hasDetails = details && details.length > 0;
  return (
    <div className="rounded-xl bg-slate-950/60 border border-slate-800 hover:border-slate-700 transition-colors">
      <button
        onClick={() => hasDetails && setOpen(!open)}
        className={`w-full flex items-center justify-between px-3 py-2.5 ${hasDetails ? "cursor-pointer" : "cursor-default"}`}
        data-testid={`entrata-row-${label.replace(/\s+/g,'-')}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-slate-500">{icon}</span>
          <span className="text-sm font-bold truncate">{label}</span>
          {hasDetails && <span className="text-slate-600">{open ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}</span>}
        </div>
        <span className={`font-mono font-black text-sm ${color}`}>
          {value >= 0 ? "+" : ""}{money(value)}<span className="text-[10px] text-slate-500 ml-1">cr</span>
        </span>
      </button>
      {open && hasDetails && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-800 space-y-1">
          {details.map((d, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-slate-400">{d.label || d.name || d}</span>
              <span className="font-mono text-slate-300">{typeof d.amount === "number" ? money(d.amount) : d.amount}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BudgetFinaleCard({ s }) {
  const saldo = Number(s.saldo || 0);
  const positive = saldo >= 0;
  const gaugeData = [{ name: "budget", value: Math.min(100, Math.max(0, ((s.new_budget || 0) / Math.max(s.new_budget + 200, 500)) * 100)) }];
  return (
    <div className={`border rounded-3xl p-5 shadow-2xl ${positive
      ? "border-emerald-500/40 bg-gradient-to-br from-emerald-900/40 via-slate-900 to-slate-950 shadow-emerald-500/10"
      : "border-rose-500/40 bg-gradient-to-br from-rose-900/40 via-slate-900 to-slate-950 shadow-rose-500/10"
    }`} data-testid="budget-finale-card">
      <div className="text-[10px] uppercase tracking-[0.3em] font-black flex items-center gap-2 mb-1">
        <Star size={12} className={positive ? "text-emerald-400" : "text-rose-400"}/> Budget nuova stagione
      </div>
      <div className="text-4xl font-mono font-black mt-3" data-testid="new-budget-value">
        {money(s.new_budget)}<span className="text-sm text-slate-500 ml-1">cr</span>
      </div>
      <div className={`text-sm mt-1 font-bold ${positive ? "text-emerald-300" : "text-rose-300"}`}>
        {positive ? "↗ +" : "↘ "}{money(saldo)} saldo
      </div>
      <div className="h-32 mt-4">
        <ResponsiveContainer>
          <RadialBarChart innerRadius="65%" outerRadius="100%" barSize={12} data={gaugeData} startAngle={90} endAngle={-270}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false}/>
            <RadialBar dataKey="value" cornerRadius={10} fill={positive ? "#34D399" : "#F87171"} background={{ fill: "#1e293b" }} />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
        <div className="rounded-lg bg-slate-950/50 p-2">
          <div className="text-slate-500 text-[9px] uppercase tracking-widest">Ranking staz.</div>
          <div className="font-mono font-black text-amber-300 text-sm">+{s.ranking?.season || 0}</div>
        </div>
        <div className="rounded-lg bg-slate-950/50 p-2">
          <div className="text-slate-500 text-[9px] uppercase tracking-widest">Rank storico</div>
          <div className="font-mono font-black text-white text-sm">{Math.round(s.ranking?.new_total_after_apply || 0)}</div>
        </div>
      </div>
    </div>
  );
}

function RevenueBreakdown({ s }) {
  const data = [
    { name: "TV", value: s.entrate.tv_rights },
    { name: "Merch", value: s.entrate.merch_base },
    { name: "Reval.", value: s.entrate.merch_revaluation },
    { name: "Premi", value: s.entrate.prizes },
    { name: "Stadio", value: s.entrate.stadium_net },
    { name: "Extra", value: s.entrate.extra_total },
    { name: "Stipendi", value: -Math.abs(s.uscite.salaries) },
  ];
  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="revenue-breakdown">
      <h3 className="font-black uppercase text-sm tracking-wider mb-3">📊 Ripartizione voci</h3>
      <div className="h-64">
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
            <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
            <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }}/>
            <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12 }}
              formatter={(v) => [`${money(v)} cr`, ""]}/>
            <Bar dataKey="value" isAnimationActive animationDuration={800}>
              {data.map((d, i) => (
                <rect key={i} fill={d.value >= 0 ? "#34D399" : "#F87171"}/>
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* -------------------- Admin panel: Piazzamenti + Apply -------------------- */

function MultiSeasonHistory({ history, teamId, teamName }) {
  const chartData = useMemo(() => {
    if (!history || !teamId) return [];
    const asc = [...history].sort((a, b) => a.season - b.season);
    return asc.map(s => {
      const t = (s.teams || []).find(x => x.team_id === teamId);
      const f = t?.finance || {};
      const entrate = f.entrate?.total ?? (
        // fallback per record senza breakdown finance
        (Number(t?.tv_rights || 0) + Number(t?.merch || 0) + Number(t?.stadium || 0) + Number(t?.prize || 0))
      );
      const uscite = f.uscite?.total ?? Math.abs(Number(t?.salaries || 0));
      const saldo = f ? (t?.net_delta ?? (entrate - uscite)) : (t?.net_delta || 0);
      return {
        season: `S${s.season}`,
        Entrate: Math.round(Number(entrate || 0)),
        Uscite: -Math.abs(Math.round(Number(uscite || 0))),
        Saldo: Math.round(Number(saldo || 0)),
        Budget: Math.round(Number(t?.budget_end || 0)),
      };
    });
  }, [history, teamId]);

  const hasData = chartData.length > 0;

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="multi-season-history">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
          <BarChart3 size={16} className="text-fuchsia-400"/> 📚 Storico voci multi-stagione — {teamName}
        </h3>
        <span className="text-xs text-slate-500">{chartData.length} stagioni archiviate</span>
      </div>
      {hasData ? (
        <div className="h-72" data-testid="multi-season-chart-container">
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <defs>
                <linearGradient id="grEntrateH" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34D399" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#34D399" stopOpacity={0.05}/>
                </linearGradient>
                <linearGradient id="grUsciteH" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F87171" stopOpacity={0.05}/>
                  <stop offset="100%" stopColor="#F87171" stopOpacity={0.8}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
              <XAxis dataKey="season" tick={{ fill: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}/>
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }}/>
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12, fontSize: 12 }}
                formatter={(v) => [`${money(v)} cr`, ""]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle"/>
              <Area type="monotone" dataKey="Entrate" stroke="#34D399" strokeWidth={2} fill="url(#grEntrateH)" isAnimationActive animationDuration={900}/>
              <Area type="monotone" dataKey="Uscite" stroke="#F87171" strokeWidth={2} fill="url(#grUsciteH)" isAnimationActive animationDuration={900}/>
              <Line type="monotone" dataKey="Saldo" stroke="#FBBF24" strokeWidth={3} dot={{ r: 5, fill: '#FBBF24' }} activeDot={{ r: 7 }} isAnimationActive animationDuration={900}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-center text-slate-500 text-sm py-10 border border-dashed border-slate-800 rounded-2xl">
          📚 Nessuna stagione archiviata. Applica il saldo di fine stagione per iniziare a costruire lo storico.
        </div>
      )}
      <div className="text-[10px] text-slate-500 mt-2">
        💡 Il grafico traccia <strong className="text-emerald-300">Entrate</strong> (area verde), <strong className="text-rose-300">Uscite</strong> (area rossa, negativa) e <strong className="text-amber-300">Saldo</strong> (linea gialla) di questa squadra in ogni stagione archiviata.
      </div>
    </div>
  );
}


function AdminResultsPanel({ leagueId, season, competitions, teams, currentResults, financeApplied, onSaved, onApply, onResetFlag, applyBusy }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(currentResults || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setResults(currentResults || {}); }, [currentResults]);

  const setPos = (compId, teamId, position) => {
    setResults(prev => {
      const list = [...(prev[compId] || [])].filter(r => r.team_id !== teamId);
      if (position) list.push({ team_id: teamId, position: Number(position) });
      list.sort((a,b) => a.position - b.position);
      return { ...prev, [compId]: list };
    });
  };
  const getPos = (compId, teamId) => {
    return (results[compId] || []).find(r => r.team_id === teamId)?.position || "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/finances/season-results`, { results });
      toast.success("✅ Piazzamenti salvati");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-amber-500/30 rounded-3xl bg-gradient-to-br from-amber-900/20 via-slate-900 to-slate-950 shadow-2xl shadow-black/40 overflow-hidden" data-testid="admin-results-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-amber-500/5 transition-colors"
        data-testid="admin-results-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-xl">🏛️</div>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">Admin · Fine stagione</div>
            <div className="font-black text-white">Piazzamenti competizioni & Applica saldo</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {financeApplied && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-black">
              ✅ Saldo applicato
            </span>
          )}
          {open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
        </div>
      </button>
      {open && (
        <div className="border-t border-amber-500/20 p-4 space-y-4">
          {competitions.length === 0 ? (
            <div className="text-slate-500 text-sm py-4 text-center">Nessuna competizione configurata. Aggiungile in Admin → Competizioni.</div>
          ) : (
            <div className="space-y-3">
              {competitions.map(c => (
                <div key={c.id} className="rounded-2xl bg-slate-950/70 border border-slate-800 p-3">
                  <div className="text-xs font-black uppercase tracking-widest text-slate-300 mb-2">
                    {c.emoji} {c.name} <span className="text-slate-500 font-normal ml-1">({c.prize_positions?.length || 0} posizioni premiate)</span>
                  </div>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {teams.map(t => (
                      <div key={t.team_id} className="flex items-center gap-2 rounded-lg bg-slate-900/60 border border-slate-800 px-2 py-1.5">
                        {logoSrc(t.logo_url)
                          ? <img src={logoSrc(t.logo_url)} alt="" className="w-5 h-5 rounded object-cover"/>
                          : <span className="w-5 h-5 rounded bg-slate-800 flex items-center justify-center text-[10px]">⚽</span>}
                        <span className="flex-1 text-xs font-bold truncate">{t.name}</span>
                        <input
                          type="number"
                          min="0"
                          max="30"
                          value={getPos(c.id, t.team_id)}
                          onChange={(e)=>setPos(c.id, t.team_id, e.target.value)}
                          placeholder="—"
                          className="w-14 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs font-mono font-bold text-center"
                          data-testid={`pos-${c.id}-${t.team_id}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={save} disabled={saving}
              data-testid="save-results-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider"
            >
              <Save size={14}/> {saving ? "Salvo…" : "Salva piazzamenti"}
            </button>
            {!financeApplied ? (
              <button
                onClick={onApply} disabled={applyBusy}
                data-testid="apply-saldo-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider"
              >
                <Check size={14}/> {applyBusy ? "Applico…" : "💰 Applica saldo alla lega"}
              </button>
            ) : (
              <button
                onClick={onResetFlag}
                data-testid="reset-flag-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-700 text-slate-400 hover:border-rose-500/50 hover:text-rose-300 font-bold text-xs uppercase tracking-wider"
              >
                <RefreshCcw size={14}/> Sblocca ri-applica
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* ==================== VISTA CONFRONTO MULTI-SQUADRA ==================== */
function CompareTable({ data, teams }) {
  const [sortKey, setSortKey] = useState("saldo");
  const [sortDir, setSortDir] = useState("desc");

  const rows = useMemo(() => {
    const r = (data?.teams || []).map(t => {
      const logo = teams.find(x => x.team_id === t.team_id)?.logo_url;
      return {
        team_id: t.team_id, name: t.team_name, logo,
        budget: t.patrimonio.budget,
        valore_rosa: t.patrimonio.valore_rosa,
        patrimonio: t.patrimonio.patrimonio_totale,
        tv: t.entrate.tv_rights,
        merch: t.entrate.merch_base,
        reval: t.entrate.merch_revaluation,
        prizes: t.entrate.prizes,
        stadium: t.entrate.stadium_net,
        entrate: t.entrate.total,
        stipendi: t.uscite.salaries,
        uscite: t.uscite.total,
        saldo: t.saldo,
        new_budget: t.new_budget,
        ranking_season: t.ranking.season,
      };
    });
    r.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0;
      if (typeof va === "string") return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return r;
  }, [data, teams, sortKey, sortDir]);

  const totals = data?.totals || { entrate: 0, uscite: 0, saldo: 0, patrimonio_totale: 0 };

  const setSort = (k) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const SortH = ({ k, label, align="right" }) => {  // eslint-disable-line react/no-unstable-nested-components
    return (
      <th className={`px-2 py-3 text-${align} cursor-pointer hover:text-emerald-300 whitespace-nowrap select-none`} onClick={()=>setSort(k)}>
        {label} {sortKey === k && (sortDir === "asc" ? "↑" : "↓")}
      </th>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header totali lega */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <TotalCard emoji="💰" label="Entrate lega" value={money(totals.entrate)} tone="emerald"/>
        <TotalCard emoji="💸" label="Uscite lega" value={money(totals.uscite)} tone="rose"/>
        <TotalCard emoji="📈" label="Saldo netto" value={money(totals.saldo)} tone={totals.saldo >= 0 ? "emerald" : "rose"}/>
        <TotalCard emoji="🏆" label="Patrimonio lega" value={money(totals.patrimonio_totale)} tone="amber"/>
      </div>

      {/* Tabella confronto */}
      <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 shadow-2xl shadow-black/40 overflow-hidden" data-testid="compare-table">
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h3 className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
              <BarChart3 size={16} className="text-emerald-400"/> Confronto Entrate &amp; Uscite
            </h3>
            <p className="text-slate-500 text-xs mt-0.5">Clicca su una colonna per ordinare · Vista comparativa completa</p>
          </div>
          <span className="text-[10px] text-slate-500 uppercase tracking-widest">{rows.length} squadre</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[1200px]">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-400 font-black">
              <tr>
                <th className="px-3 py-3 text-left cursor-pointer hover:text-white" onClick={()=>setSort("name")}>Squadra</th>
                <SortH k="budget" label="💰 Budget"/>
                <SortH k="valore_rosa" label="📊 V.Rosa"/>
                <SortH k="tv" label="📺 TV"/>
                <SortH k="merch" label="🛍️ Merch"/>
                <SortH k="reval" label="✨ Reval."/>
                <SortH k="prizes" label="🏆 Premi"/>
                <SortH k="stadium" label="🏟️ Stadio"/>
                <SortH k="entrate" label="↗ Entrate"/>
                <SortH k="stipendi" label="💵 Stip."/>
                <SortH k="saldo" label="⚖️ Saldo"/>
                <SortH k="new_budget" label="🆕 New Bud."/>
                <SortH k="ranking_season" label="⭐ Rank"/>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.team_id} className="border-t border-slate-800 hover:bg-slate-800/40 transition-colors" data-testid={`compare-row-${r.team_id}`}>
                  <td className="px-3 py-2.5 font-bold whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-mono w-5 text-xs">
                        {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i+1}°`}
                      </span>
                      {logoSrc(r.logo)
                        ? <img src={logoSrc(r.logo)} alt="" className="w-5 h-5 rounded object-cover"/>
                        : <span className="w-5 h-5 rounded bg-slate-800 flex items-center justify-center text-[9px]">⚽</span>}
                      <span className="truncate max-w-[140px]">{r.name}</span>
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono">{money(r.budget)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-emerald-300">{money(r.valore_rosa)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-sky-300">{money(r.tv)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-fuchsia-300">{money(r.merch)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-fuchsia-200">{money(r.reval)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-amber-300">{money(r.prizes)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-orange-300">{money(r.stadium)}</td>
                  <td className="px-2 py-2.5 text-right font-mono font-black text-emerald-300">+{money(r.entrate)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-rose-300">-{money(r.stipendi)}</td>
                  <td className={`px-2 py-2.5 text-right font-mono font-black ${r.saldo >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {r.saldo >= 0 ? "↗" : "↘"} {money(r.saldo)}
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono font-black text-white bg-slate-950/60">{money(r.new_budget)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-amber-300">+{r.ranking_season}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Barchart classifica saldi */}
      <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="compare-saldi-chart">
        <h3 className="font-black uppercase text-sm tracking-wider mb-3">📊 Classifica saldi finali</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={rows.map(r => ({ name: r.name, Saldo: r.saldo, Entrate: r.entrate, Stipendi: -r.stipendi }))} margin={{ top: 10, right: 20, bottom: 50, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70}/>
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }}/>
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12 }}
                formatter={(v) => [`${money(v)} cr`, ""]}/>
              <Bar dataKey="Entrate" fill="#34D399" isAnimationActive animationDuration={900}/>
              <Bar dataKey="Stipendi" fill="#F87171" isAnimationActive animationDuration={900}/>
              <Bar dataKey="Saldo" fill="#FBBF24" isAnimationActive animationDuration={900}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function TotalCard({ emoji, label, value, tone }) {
  const toneMap = {
    emerald: "border-emerald-500/40 text-emerald-300 from-emerald-500/10",
    rose: "border-rose-500/40 text-rose-300 from-rose-500/10",
    amber: "border-amber-500/40 text-amber-300 from-amber-500/10",
  };
  return (
    <div className={`rounded-2xl border ${toneMap[tone]} bg-gradient-to-br to-slate-950 p-4 shadow-xl`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
        <span>{emoji}</span> {label}
      </div>
      <div className="text-2xl font-mono font-black mt-1">{value}<span className="text-xs text-slate-500 ml-1">cr</span></div>
    </div>
  );
}


function TeamJourneyPanel({ leagueId, teamId, teamName, nHistorySeasons }) {
  const [open, setOpen] = useState(false);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leagues/${leagueId}/finances/team-journey/${teamId}`);
      if (r.data?.journey) {
        setJourney(r.data.journey);
        setMeta(r.data);
      } else {
        setJourney(null);
      }
    } catch { setJourney(null); }
    finally { setLoading(false); }
  };

  const generate = async (refresh = false) => {
    setLoading(true);
    try {
      const url = `/leagues/${leagueId}/finances/team-journey/${teamId}${refresh ? "?refresh=true" : ""}`;
      const r = await api.post(url);
      setJourney(r.data.journey);
      setMeta(r.data);
      toast.success(refresh ? "🔄 Journey rigenerato" : "🎬 Journey generato dall'AI");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const toggle = async () => {
    if (!open && journey === null) await load();
    setOpen(!open);
  };

  const canGenerate = nHistorySeasons >= 2;

  return (
    <div className="border border-indigo-500/30 rounded-3xl bg-gradient-to-br from-indigo-900/20 via-slate-900 to-slate-950 shadow-2xl shadow-black/40 overflow-hidden" data-testid="team-journey-panel">
      <button
        onClick={toggle}
        data-testid="team-journey-toggle"
        className="w-full flex items-center justify-between p-4 hover:bg-indigo-500/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center text-xl">🎬</div>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-300 font-black">AI Coach · Journey multi-stagione</div>
            <div className="font-black text-white">Racconto del percorso — {teamName}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500 uppercase tracking-widest">{nHistorySeasons} stagioni archiviate</span>
          {open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
        </div>
      </button>
      {open && (
        <div className="border-t border-indigo-500/20 p-5">
          {!canGenerate ? (
            <div className="text-center py-6 text-slate-400 text-sm">
              📚 Servono almeno <strong className="text-indigo-300">2 stagioni archiviate</strong> per generare il journey.
              Attualmente: <strong>{nHistorySeasons}</strong>. Chiudi altre stagioni con &quot;Applica saldo&quot; in Finanze.
            </div>
          ) : loading ? (
            <div className="text-center py-8 text-slate-400 text-sm animate-pulse">
              🎬 L&apos;AI Coach sta rileggendo tutte le tue stagioni…
            </div>
          ) : journey ? (
            <>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-1.5">
                    <div className="text-[9px] uppercase text-slate-500 font-bold">Stagioni</div>
                    <div className="font-mono font-black text-indigo-300">{meta.seasons_covered || nHistorySeasons}</div>
                  </div>
                  <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-1.5">
                    <div className="text-[9px] uppercase text-slate-500 font-bold">Δ Patrimonio</div>
                    <div className={`font-mono font-black ${(meta.delta_patrimonio || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {(meta.delta_patrimonio || 0) >= 0 ? "+" : ""}{money(meta.delta_patrimonio || 0)} cr
                    </div>
                  </div>
                  <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-1.5">
                    <div className="text-[9px] uppercase text-slate-500 font-bold">Trofei</div>
                    <div className="font-mono font-black text-amber-300">{meta.trophies_won || 0}</div>
                  </div>
                </div>
                <button
                  onClick={()=>generate(true)}
                  data-testid="team-journey-refresh"
                  className="text-slate-400 hover:text-indigo-300 flex items-center gap-1 text-xs font-black"
                >
                  <RefreshCcw size={12}/> Rigenera
                </button>
              </div>
              <MarkdownRecap text={journey}/>
              {meta.created_at && (
                <div className="text-[9px] text-slate-600 mt-3 uppercase tracking-widest">
                  Claude Sonnet 5 · Generato il {new Date(meta.created_at).toLocaleString("it-IT")}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6">
              <div className="text-slate-400 text-sm mb-3">Nessun journey ancora generato per {teamName}.</div>
              <button
                onClick={()=>generate(false)}
                data-testid="team-journey-generate"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white font-black text-xs uppercase tracking-wider"
              >
                <Sparkles size={14}/> Genera con AI Coach
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Copiato da LeagueStorico per non fare import circolare
function MarkdownRecap({ text }) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const out = [];
  let key = 0;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { out.push(<div key={key++} className="h-2"/>); continue; }
    if (line.startsWith("# ")) {
      out.push(<h3 key={key++} className="text-xl font-black text-indigo-200 mt-4 mb-1">{line.slice(2)}</h3>);
    } else if (line.startsWith("## ")) {
      out.push(<h4 key={key++} className="text-base font-black text-amber-300 mt-3 mb-1">{line.slice(3)}</h4>);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      out.push(<li key={key++} className="text-sm text-slate-300 ml-4 list-disc" dangerouslySetInnerHTML={{ __html: mdInline(line.slice(2)) }}/>);
    } else {
      out.push(<p key={key++} className="text-sm text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: mdInline(line) }}/>);
    }
  }
  return <div className="prose prose-invert prose-sm max-w-none">{out}</div>;
}

function mdInline(s) {
  const esc = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-black">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="text-amber-200">$1</em>')
    .replace(/`([^`]+)`/g, '<code class="text-emerald-300 bg-slate-950 px-1 rounded text-xs">$1</code>');
}
